/**
 * Service Unifié de Disponibilité des Véhicules
 *
 * Single Source of Truth pour toutes les vérifications de disponibilité.
 *
 * Principe SOLID:
 * - Single Responsibility: Vérifier la disponibilité des véhicules uniquement
 * - Open/Closed: Extensible via interfaces sans modification
 * - Dependency Inversion: Dépend des abstractions (types/interfaces)
 *
 * Principe KISS: Logique simple et prévisible
 */

import { supabase } from "@/lib/supabase";
import { trackOwnerFleetReminderUpdate } from "@/lib/fleet-reminder/track-fleet-reminder";
import { BLOCKING_BOOKING_STATUSES } from "@/lib/types/booking-status";
import { BookingStatus } from "@/types/booking";
import { startOfDay, eachDayOfInterval } from "date-fns";
import { formatDateLocal } from "@/lib/utils/date";
import { collectConflictingVehicleIds } from "@/lib/utils/booking-overlap";
import { isStayFullyAvailable } from "@/lib/services/stay-availability";

// ============================================================================
// TYPES
// ============================================================================

export type BlockedDateReason = "maintenance" | "manual" | "external_booking" | "other";

export interface BlockedDate {
  id: string;
  vehicle_id: string;
  blocked_date: string;
  reason: BlockedDateReason;
  note?: string | null;
  created_by?: string | null;
  created_at: string | null;
}

export type AvailabilityDateStatus = "available" | "booked" | "blocked" | "maintenance";

export interface AvailabilityDateInfo {
  date: Date;
  status: AvailabilityDateStatus;
  reason?: string;
  bookingId?: string;
  blockId?: string;
  renterName?: string;
}

export interface DateAvailabilityMap {
  [dateString: string]: AvailabilityDateInfo;
}

export interface BookingConflict {
  id: string;
  start_date: string;
  end_date: string;
  status: BookingStatus;
}

export interface AvailabilityCheckResult {
  available: boolean;
  conflicts: AvailabilityDateInfo[];
  conflictingBookings?: BookingConflict[];
  error?: string;
}

interface CacheEntry {
  data: DateAvailabilityMap;
  timestamp: number;
}

// ============================================================================
// SERVICE
// ============================================================================

class VehicleAvailabilityService {
  // Cache TTL aligned with calendar fallback poll (Realtime handles fast updates)
  private cache: Map<string, CacheEntry> = new Map();
  private readonly CACHE_TTL = 30_000; // 30 secondes
  // Protection contre les erreurs réseau répétées
  private errorCount: Map<string, number> = new Map();
  private readonly MAX_ERRORS = 3; // Arrêter après 3 erreurs consécutives

  /**
   * Vérifie si un véhicule est disponible pour une plage de dates
   *
   * Cette méthode est la source unique de vérité pour toutes les vérifications.
   * Elle combine:
   * - Les réservations avec statuts bloquants
   * - Les dates bloquées manuellement
   * - Le statut du véhicule lui-même
   *
   * @param vehicleId - ID du véhicule (utilise vehicle_id, pas car_id)
   * @param startDate - Date de début (inclusive)
   * @param endDate - Date de fin (inclusive)
   * @param useCache - Utiliser le cache si disponible (défaut: true)
   * @returns Résultat de la vérification avec détails des conflits
   */
  async checkAvailability(
    vehicleId: string,
    startDate: Date,
    endDate: Date,
    useCache: boolean = true
  ): Promise<AvailabilityCheckResult> {
    try {
      // Normaliser les dates (start of day pour éviter les problèmes de timezone)
      const normalizedStart = startOfDay(startDate);
      const normalizedEnd = startOfDay(endDate);

      // Validation des dates
      if (isNaN(normalizedStart.getTime()) || isNaN(normalizedEnd.getTime())) {
        return {
          available: false,
          conflicts: [],
          error: "Format de date invalide",
        };
      }

      if (normalizedStart >= normalizedEnd) {
        return {
          available: false,
          conflicts: [],
          error: "La date de début doit être antérieure à la date de fin",
        };
      }

      // Clé de cache
      const cacheKey = `${vehicleId}-${formatDateLocal(normalizedStart)}-${formatDateLocal(normalizedEnd)}`;

      // Vérifier le cache
      if (useCache) {
        const cached = this.cache.get(cacheKey);
        const now = Date.now();

        if (cached && now - cached.timestamp < this.CACHE_TTL) {
          // Utiliser le cache si récent
          return this.extractAvailabilityFromMap(cached.data, normalizedStart, normalizedEnd);
        }
      }

      // Vérifier en base de données (utiliser le cache par défaut)
      const map = await this.getAvailabilityMap(vehicleId, normalizedStart, normalizedEnd, useCache);

      // Mettre à jour le cache
      this.cache.set(cacheKey, {
        data: map,
        timestamp: Date.now(),
      });

      return this.extractAvailabilityFromMap(map, normalizedStart, normalizedEnd);
    } catch (error) {
      console.error("[VehicleAvailabilityService] Erreur vérification disponibilité:", error);
      // En cas d'erreur, retourner non disponible par sécurité
      return {
        available: false,
        conflicts: [
          {
            date: startOfDay(startDate),
            status: "blocked",
            reason: "Erreur de vérification de disponibilité",
          },
        ],
        error: error instanceof Error ? error.message : "Erreur inconnue",
      };
    }
  }

  /**
   * Récupère la carte de disponibilité complète pour un véhicule
   * Combine les réservations et les dates bloquées
   *
   * Méthode publique pour permettre l'accès à la carte complète
   */
  async getAvailabilityMap(
    vehicleId: string,
    startDate: Date,
    endDate: Date,
    useCache: boolean = true
  ): Promise<DateAvailabilityMap> {
    const cacheKey = `${vehicleId}-${formatDateLocal(startDate)}-${formatDateLocal(endDate)}`;
    const errorKey = `${vehicleId}-errors`;

    // Vérifier si on a trop d'erreurs récentes pour éviter les boucles infinies
    const errorCount = this.errorCount.get(errorKey) || 0;
    if (errorCount >= this.MAX_ERRORS) {
      console.warn(`[VehicleAvailabilityService] Trop d'erreurs pour ${vehicleId}, utilisation du cache uniquement`);
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL * 2) {
        return cached.data;
      }
      // Retourner une map vide plutôt que de continuer à essayer
      return {};
    }

    // Vérifier le cache seulement si activé
    if (useCache) {
      const cached = this.cache.get(cacheKey);
      const now = Date.now();
      if (cached && now - cached.timestamp < this.CACHE_TTL) {
        return cached.data;
      }
    }

    const map: DateAvailabilityMap = {};

    try {
      // 1. Vérifier que le véhicule existe et est disponible
      const { data: vehicle, error: vehicleError } = await supabase
        .from("vehicles")
        .select("id, status")
        .eq("id", vehicleId)
        .single();

      if (vehicleError || !vehicle) {
        this.incrementErrorCount(errorKey);
        throw new Error(`Véhicule non trouvé: ${vehicleError?.message || "Inconnu"}`);
      }

      // Réinitialiser le compteur d'erreurs en cas de succès
      this.errorCount.delete(errorKey);

      if (vehicle.status !== "available") {
        // Si le véhicule n'est pas disponible, marquer toutes les dates comme bloquées
        const dates = eachDayOfInterval({ start: startDate, end: endDate });
        for (const date of dates) {
          const dateStr = formatDateLocal(date);
          map[dateStr] = {
            date: new Date(date),
            status: "blocked",
            reason: `Véhicule ${vehicle.status}`,
          };
        }
        // Mettre en cache
        this.cache.set(cacheKey, {
          data: map,
          timestamp: Date.now(),
        });
        return map;
      }

      // 2. Initialiser toutes les dates comme disponibles
      const dates = eachDayOfInterval({ start: startDate, end: endDate });
      for (const date of dates) {
        const dateStr = formatDateLocal(date);
        map[dateStr] = {
          date: new Date(date),
          status: "available",
        };
      }

      // 3. Récupérer les dates bloquées
      try {
        const blockedDates = await this.getBlockedDatesInRange(vehicleId, startDate, endDate);
        for (const blocked of blockedDates) {
          const dateStr = blocked.blocked_date;
          if (map[dateStr]) {
            map[dateStr] = {
              date: new Date(dateStr),
              status: blocked.reason === "maintenance" ? "maintenance" : "blocked",
              reason:
                blocked.reason === "maintenance"
                  ? "Maintenance programmée"
                  : blocked.note || "Bloqué par le propriétaire",
              blockId: blocked.id,
            };
          }
        }
      } catch (blockedError) {
        console.error("[VehicleAvailabilityService] Erreur récupération dates bloquées:", blockedError);
        // Continuer même si erreur sur dates bloquées
      }

      // 4. Récupérer les réservations avec statuts bloquants
      try {
        const bookings = await this.getBlockingBookings(vehicleId, startDate, endDate);

        // 5. Marquer les réservations (priorité sur les blocages)
        for (const booking of bookings) {
          try {
            const bookingStart = startOfDay(new Date(booking.start_date));
            const bookingEnd = startOfDay(new Date(booking.end_date));
            const bookingDates = eachDayOfInterval({ start: bookingStart, end: bookingEnd });

            for (const date of bookingDates) {
              const dateStr = formatDateLocal(date);
              if (map[dateStr]) {
                map[dateStr] = {
                  date: new Date(date),
                  status: "booked",
                  reason: booking.renter_name ? `Réservé par ${booking.renter_name}` : "Réservé",
                  bookingId: booking.id,
                  renterName: booking.renter_name,
                };
              }
            }
          } catch (e) {
            console.error("[VehicleAvailabilityService] Erreur traitement réservation:", e, booking);
          }
        }
      } catch (bookingsError) {
        console.error("[VehicleAvailabilityService] Erreur récupération réservations:", bookingsError);
        // Continuer même si erreur sur réservations
      }

      // Mettre en cache le résultat
      this.cache.set(cacheKey, {
        data: map,
        timestamp: Date.now(),
      });

      return map;
    } catch (error) {
      this.incrementErrorCount(errorKey);
      // Si erreur réseau, essayer de retourner le cache si disponible
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        const cached = this.cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < this.CACHE_TTL * 2) {
          console.warn(`[VehicleAvailabilityService] Erreur réseau, utilisation du cache pour ${vehicleId}`);
          return cached.data;
        }
      }
      throw error;
    }
  }

  /**
   * Incrémente le compteur d'erreurs pour un véhicule
   */
  private incrementErrorCount(errorKey: string): void {
    const current = this.errorCount.get(errorKey) || 0;
    this.errorCount.set(errorKey, current + 1);

    // Réinitialiser après un délai pour permettre de réessayer
    setTimeout(() => {
      this.errorCount.delete(errorKey);
    }, 60000); // Réinitialiser après 1 minute
  }

  /**
   * Récupère les réservations qui bloquent la disponibilité
   * Utilise les statuts standardisés depuis booking-status.ts
   */
  private async getBlockingBookings(
    vehicleId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Array<{
    id: string;
    start_date: string;
    end_date: string;
    status: BookingStatus;
    renter_name?: string;
  }>> {
    const startStr = formatDateLocal(startDate);
    const endStr = formatDateLocal(endDate);

    // Convertir les statuts en array pour la requête
    const blockingStatusesArray = [...BLOCKING_BOOKING_STATUSES];

    // Overlap: start_date <= rangeEnd AND end_date >= rangeStart (AND, not OR —
    // OR over-fetches and defeats availability indexes).
    const { data, error } = await supabase
      .from("bookings")
      .select(
        `
        id,
        start_date,
        end_date,
        status
      `
      )
      .eq("vehicle_id", vehicleId)
      .in("status", blockingStatusesArray)
      .lte("start_date", endStr)
      .gte("end_date", startStr);

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur récupération réservations:", error);
      throw new Error(`Erreur récupération réservations: ${error.message}`);
    }

    return (data || []).map((booking) => ({
      id: booking.id,
      start_date: booking.start_date,
      end_date: booking.end_date,
      status: booking.status as BookingStatus,
      renter_name: undefined,
    }));
  }

  /**
   * Récupère toutes les dates bloquées pour un véhicule
   */
  async getBlockedDates(vehicleId: string): Promise<BlockedDate[]> {
    const { data, error } = await supabase
      .from("vehicle_blocked_dates")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .order("blocked_date", { ascending: true });

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur récupération dates bloquées:", error);
      throw new Error(`Erreur récupération dates bloquées: ${error.message}`);
    }

    return (data || []) as BlockedDate[];
  }

  /**
   * Récupère les dates bloquées dans une plage
   */
  async getBlockedDatesInRange(
    vehicleId: string,
    startDate: Date,
    endDate: Date
  ): Promise<BlockedDate[]> {
    const startStr = formatDateLocal(startDate);
    const endStr = formatDateLocal(endDate);

    const { data, error } = await supabase
      .from("vehicle_blocked_dates")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .gte("blocked_date", startStr)
      .lte("blocked_date", endStr)
      .order("blocked_date", { ascending: true });

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur récupération dates bloquées:", error);
      throw new Error(`Erreur récupération dates bloquées: ${error.message}`);
    }

    return (data || []) as BlockedDate[];
  }

  /**
   * Extrait les conflits d'une carte de disponibilité
   */
  private extractAvailabilityFromMap(
    map: DateAvailabilityMap,
    startDate: Date,
    endDate: Date
  ): AvailabilityCheckResult {
    const conflicts: AvailabilityDateInfo[] = [];
    const conflictingBookings: BookingConflict[] = [];
    const bookingIdsSeen = new Set<string>();

    const dates = eachDayOfInterval({ start: startDate, end: endDate });
    for (const date of dates) {
      const dateStr = formatDateLocal(date);
      const status = map[dateStr];

      if (status && status.status !== "available") {
        conflicts.push(status);

        // Collecter les IDs de réservations en conflit (sans doublons)
        if (status.bookingId && !bookingIdsSeen.has(status.bookingId)) {
          bookingIdsSeen.add(status.bookingId);
          // Trouver la réservation complète dans la map
          // (on pourrait améliorer ça en stockant plus d'infos dans la map)
        }
      }
    }

    return {
      available: conflicts.length === 0,
      conflicts,
      conflictingBookings: conflictingBookings.length > 0 ? conflictingBookings : undefined,
    };
  }

  /**
   * Bloquer des dates pour un véhicule
   */
  async blockDates(
    vehicleId: string,
    dates: string[],
    reason: BlockedDateReason = "manual",
    note?: string
  ): Promise<BlockedDate[]> {
    // Préparer les données pour insertion
    const blockedDates = dates.map((date) => ({
      vehicle_id: vehicleId,
      blocked_date: date,
      reason,
      note: note || null,
    }));

    // Utiliser upsert pour gérer les doublons (remplacer si existe déjà)
    let { data, error } = await supabase
      .from("vehicle_blocked_dates")
      .upsert(blockedDates, {
        onConflict: "vehicle_id,blocked_date",
        ignoreDuplicates: false,
      })
      .select();
    
    // Si erreur avec onConflict, essayer avec insert et gérer les doublons manuellement
    if (error && error.code === "23505") {
      // Supprimer les dates existantes d'abord
      const dateStrings = blockedDates.map(bd => bd.blocked_date);
      await supabase
        .from("vehicle_blocked_dates")
        .delete()
        .eq("vehicle_id", vehicleId)
        .in("blocked_date", dateStrings);
      
      // Puis insérer
      const insertResult = await supabase
        .from("vehicle_blocked_dates")
        .insert(blockedDates)
        .select();
      
      data = insertResult.data;
      error = insertResult.error;
    }

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur blocage dates:", error);
      throw new Error(`Erreur blocage dates: ${error.message}`);
    }

    // Invalider le cache après modification
    this.invalidateCache(vehicleId);
    await this.stampAvailabilityConfirmed([vehicleId]);

    return (data || []) as BlockedDate[];
  }

  /**
   * Débloquer des dates par leurs IDs
   */
  async unblockDatesById(vehicleId: string, blockIds: string[]): Promise<void> {
    const { error } = await supabase
      .from("vehicle_blocked_dates")
      .delete()
      .eq("vehicle_id", vehicleId)
      .in("id", blockIds);

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur déblocage dates:", error);
      throw new Error(`Erreur déblocage dates: ${error.message}`);
    }

    // Invalider le cache après modification
    this.invalidateCache(vehicleId);
    await this.stampAvailabilityConfirmed([vehicleId]);
  }

  /**
   * Débloquer des dates par date
   */
  async unblockDatesByDate(vehicleId: string, dates: string[]): Promise<void> {
    const { error } = await supabase
      .from("vehicle_blocked_dates")
      .delete()
      .eq("vehicle_id", vehicleId)
      .in("blocked_date", dates);

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur déblocage dates:", error);
      throw new Error(`Erreur déblocage dates: ${error.message}`);
    }

    // Invalider le cache après modification
    this.invalidateCache(vehicleId);
    await this.stampAvailabilityConfirmed([vehicleId]);
  }

  /**
   * Stamp availability_confirmed_at without changing blocks.
   * Failures are non-blocking for the caller path (logged only).
   */
  async stampAvailabilityConfirmed(vehicleIds: string[]): Promise<number> {
    if (vehicleIds.length === 0) return 0;
    try {
      const { data, error } = await supabase.rpc("confirm_vehicle_availability", {
        p_vehicle_ids: vehicleIds,
      });
      if (error) {
        console.error("[VehicleAvailabilityService] Erreur stamp freshness:", error);
        return 0;
      }
      void trackOwnerFleetReminderUpdate();
      if ((data as number) > 0) {
        void import("@/lib/services/posthog.service").then(({ trackAgencyEvent }) => {
          trackAgencyEvent("availability_confirmed", { vehicle_count: data });
        });
      }
      return (data as number) ?? 0;
    } catch (err) {
      console.error("[VehicleAvailabilityService] Erreur stamp freshness:", err);
      return 0;
    }
  }

  /**
   * Confirm availability only (explicit freshness stamp). Throws on failure.
   */
  async confirmVehicleAvailability(vehicleIds: string[]): Promise<number> {
    if (vehicleIds.length === 0) return 0;
    const { data, error } = await supabase.rpc("confirm_vehicle_availability", {
      p_vehicle_ids: vehicleIds,
    });
    if (error) {
      console.error("[VehicleAvailabilityService] Erreur confirm availability:", error);
      throw new Error(`Erreur confirmation disponibilité: ${error.message}`);
    }
    void trackOwnerFleetReminderUpdate();
    return (data as number) ?? 0;
  }

  /**
   * Bulk block/unblock across owned vehicles (atomic RPC).
   */
  async bulkSetVehicleAvailability(
    vehicleIds: string[],
    startDate: string,
    endDate: string,
    action: "block" | "unblock",
    reason?: string | null
  ): Promise<number> {
    if (vehicleIds.length === 0) return 0;
    const { data, error } = await supabase.rpc("bulk_set_vehicle_availability", {
      p_vehicle_ids: vehicleIds,
      p_start: startDate,
      p_end: endDate,
      p_action: action,
      p_reason: reason ?? null,
    });
    if (error) {
      console.error("[VehicleAvailabilityService] Erreur bulk availability:", error);
      throw new Error(`Erreur mise à jour groupée: ${error.message}`);
    }
    vehicleIds.forEach((id) => this.invalidateCache(id));
    void trackOwnerFleetReminderUpdate();
    return (data as number) ?? 0;
  }

  /**
   * Mettre à jour une date bloquée
   */
  async updateBlockedDate(
    blockId: string,
    updates: { reason?: BlockedDateReason; note?: string }
  ): Promise<BlockedDate> {
    const { data, error } = await supabase
      .from("vehicle_blocked_dates")
      .update(updates)
      .eq("id", blockId)
      .select()
      .single();

    if (error) {
      console.error("[VehicleAvailabilityService] Erreur mise à jour date bloquée:", error);
      throw new Error(`Erreur mise à jour date bloquée: ${error.message}`);
    }

    const blockedDate = data as BlockedDate;
    // Invalider le cache après modification
    this.invalidateCache(blockedDate.vehicle_id);

    return blockedDate;
  }

  /**
   * Récupérer les statistiques de disponibilité pour un véhicule
   */
  async getAvailabilityStats(
    vehicleId: string,
    daysAhead: number = 30
  ): Promise<{
    totalDays: number;
    availableDays: number;
    bookedDays: number;
    blockedDays: number;
    maintenanceDays: number;
  }> {
    const startDate = startOfDay(new Date());
    const endDate = startOfDay(new Date());
    endDate.setDate(endDate.getDate() + daysAhead);

    const map = await this.getAvailabilityMap(vehicleId, startDate, endDate, true);

    let availableDays = 0;
    let bookedDays = 0;
    let blockedDays = 0;
    let maintenanceDays = 0;

    for (const dateStr in map) {
      switch (map[dateStr].status) {
        case "available":
          availableDays++;
          break;
        case "booked":
          bookedDays++;
          break;
        case "blocked":
          blockedDays++;
          break;
        case "maintenance":
          maintenanceDays++;
          break;
      }
    }

    const totalDays = Object.keys(map).length;

    return {
      totalDays,
      availableDays,
      bookedDays,
      blockedDays,
      maintenanceDays,
    };
  }

  /**
   * Bloquer une plage de dates (toutes les dates entre start et end)
   */
  async blockDateRange(
    vehicleId: string,
    startDate: Date,
    endDate: Date,
    reason: BlockedDateReason = "manual",
    note?: string
  ): Promise<BlockedDate[]> {
    const dates: string[] = [];
    const normalizedStart = startOfDay(startDate);
    const normalizedEnd = startOfDay(endDate);
    const current = new Date(normalizedStart);

    while (current <= normalizedEnd) {
      dates.push(formatDateLocal(current));
      current.setDate(current.getDate() + 1);
    }

    return this.blockDates(vehicleId, dates, reason, note);
  }

  /**
   * Récupérer la prochaine date disponible
   */
  async getNextAvailableDate(
    vehicleId: string,
    fromDate: Date = new Date(),
    maxDaysAhead: number = 90
  ): Promise<Date | null> {
    const normalizedFrom = startOfDay(fromDate);
    const endDate = startOfDay(new Date());
    endDate.setDate(endDate.getDate() + maxDaysAhead);

    const map = await this.getAvailabilityMap(vehicleId, normalizedFrom, endDate);

    const sortedDates = Object.keys(map).sort();
    for (const dateStr of sortedDates) {
      if (map[dateStr].status === "available") {
        return new Date(dateStr);
      }
    }

    return null;
  }

  /**
   * Invalide le cache pour un véhicule
   * À appeler après création/modification d'une réservation ou date bloquée
   */
  invalidateCache(vehicleId: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${vehicleId}-`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.cache.delete(key));
    
    // Log pour debugging en développement
    if (import.meta.env.DEV && keysToDelete.length > 0) {
      console.log(`[VehicleAvailabilityService] Cache invalidé pour véhicule ${vehicleId} (${keysToDelete.length} entrées supprimées)`);
    }
  }

  /**
   * Invalide tout le cache
   */
  clearCache(): void {
    this.cache.clear();
    // Log pour debugging en développement
    if (import.meta.env.DEV) {
      console.log("[VehicleAvailabilityService] Cache global vidé");
    }
  }

  /**
   * Nettoie le cache des entrées expirées
   */
  cleanExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp >= this.CACHE_TTL) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  /**
   * Obtient la taille actuelle du cache (pour debugging)
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Vehicles in the list that collide with a search range (bookings or blocked days).
   * Fail-open: a lookup error leaves the catalog visible instead of hiding every car.
   */
  async findUnavailableVehicleIds(
    vehicleIds: string[],
    startDate: Date,
    endDate: Date
  ): Promise<string[]> {
    const ids = [...new Set(vehicleIds.filter(Boolean))];
    if (ids.length === 0) return [];

    const normalizedStart = startOfDay(startDate);
    const normalizedEnd = startOfDay(endDate);
    if (isNaN(normalizedStart.getTime()) || isNaN(normalizedEnd.getTime())) return [];
    if (normalizedStart >= normalizedEnd) return [];

    const startStr = formatDateLocal(normalizedStart);
    const endStr = formatDateLocal(normalizedEnd);

    const { data: rpcIds, error: rpcError } = await supabase.rpc("unavailable_vehicle_ids", {
      p_vehicle_ids: ids.slice(0, 200),
      p_start: startStr,
      p_end: endStr,
    });

    if (!rpcError && Array.isArray(rpcIds)) {
      return (rpcIds as unknown[])
        .map((row) => {
          if (typeof row === "string") return row;
          if (row && typeof row === "object" && "vehicle_id" in row) {
            const id = (row as { vehicle_id?: unknown }).vehicle_id;
            return typeof id === "string" ? id : "";
          }
          return "";
        })
        .filter((id) => id.length > 0);
    }

    if (rpcError) {
      console.error("[VehicleAvailabilityService] unavailable_vehicle_ids:", rpcError.message);
    }

    const blockingStatuses = [...BLOCKING_BOOKING_STATUSES];

    const [bookingsResult, blockedResult] = await Promise.all([
      supabase
        .from("bookings")
        .select("vehicle_id, start_date, end_date")
        .in("vehicle_id", ids)
        .in("status", blockingStatuses)
        .lte("start_date", endStr)
        .gte("end_date", startStr),
      supabase
        .from("vehicle_blocked_dates")
        .select("vehicle_id, blocked_date")
        .in("vehicle_id", ids)
        .gte("blocked_date", startStr)
        .lte("blocked_date", endStr),
    ]);

    if (bookingsResult.error) {
      console.error("[VehicleAvailabilityService] bulk bookings lookup:", bookingsResult.error.message);
    }
    if (blockedResult.error) {
      console.error("[VehicleAvailabilityService] bulk blocked dates lookup:", blockedResult.error.message);
    }

    return collectConflictingVehicleIds(
      [...(bookingsResult.data ?? []), ...(blockedResult.data ?? [])],
      startStr,
      endStr
    );
  }

  /**
   * Nombre de véhicules dont getAvailabilityMap est libre sur tout le séjour.
   * Plafonné à 40 identifiants, 4 lectures en parallèle.
   */
  async countAvailableVehicles(
    vehicleIds: string[],
    startDate: Date,
    endDate: Date
  ): Promise<number> {
    const ids = [...new Set(vehicleIds.filter(Boolean))].slice(0, 40);
    if (ids.length === 0) return 0;

    let available = 0;
    for (let index = 0; index < ids.length; index += 4) {
      const chunk = ids.slice(index, index + 4);
      const maps = await Promise.all(
        chunk.map((vehicleId) =>
          this.getAvailabilityMap(vehicleId, startDate, endDate, true).catch(() => null)
        )
      );
      for (const map of maps) {
        if (map && isStayFullyAvailable(Object.values(map))) available += 1;
      }
    }
    return available;
  }

  /** Même compteur, à partir des véhicules disponibles d'une ville. */
  async countAvailableInCity(city: string, startDate: Date, endDate: Date): Promise<number> {
    const trimmed = city.trim();
    if (!trimmed) return 0;

    const { data, error } = await supabase
      .from("vehicles")
      .select("id")
      .eq("status", "available")
      .ilike("location", trimmed)
      .limit(40);

    if (error || !data) return 0;
    return this.countAvailableVehicles(
      data.map((row) => row.id),
      startDate,
      endDate
    );
  }
}

// Export d'une instance singleton
export const vehicleAvailabilityService = new VehicleAvailabilityService();

// Nettoyer le cache expiré toutes les minutes
if (typeof window !== "undefined") {
  setInterval(() => {
    vehicleAvailabilityService.cleanExpiredCache();
  }, 60000); // Toutes les minutes
}

export default vehicleAvailabilityService;

